import * as React from "react";

import { Button } from "../inputs/button";
import { Notice } from "./notice";
import type { NoticeTone } from "./notice-variants";

/**
 * A {@link Notice} for something that is WRONG, with the way out attached.
 *
 * Telling a user what broke is half a message. The half that matters is what
 * to do about it, and — in the dashboard — a control that does it. This
 * component exists so that half cannot be forgotten: both `howToResolve` and
 * `resolution` are REQUIRED, and `resolution` is a discriminated union with no
 * "none" member. There is no shape of these props that renders a dead end.
 *
 * The three shapes, and when each is honest:
 *
 * ```tsx
 * // 1. There is a control that fixes it. Prefer this. Always.
 * <ProblemNotice
 *   howToResolve="Grant this agent a mailbox on its Integrations tab."
 *   resolution={{ kind: "action", node: (
 *     <Button asChild size="sm" variant="outline"><Link to={href}>Grant a mailbox</Link></Button>
 *   ) }}
 * >
 *   list_emails is not available to this agent — no mailbox is granted to it.
 * </ProblemNotice>
 *
 * // 2. The fix is a control already on screen, so a link would just reload the
 * //    page the user is on. `howToResolve` names the control.
 * <ProblemNotice howToResolve="Re-enable it in the Tools section below."
 *                resolution={{ kind: "in-place" }}>…</ProblemNotice>
 *
 * // 3. This viewer genuinely may not act. Say who can — never render a
 * //    control that bounces them off a permission check.
 * <ProblemNotice howToResolve="A workspace admin has to grant this agent a mailbox."
 *                resolution={{ kind: "blocked", who: "Ask a workspace admin." }}>…</ProblemNotice>
 * ```
 *
 * Layout is a title bar (icon, `title`, and the action floated top-right)
 * over ONE message paragraph: `children` then `howToResolve` (then `who` when
 * blocked), each a complete sentence so they read as one paragraph.
 *
 * `node` is supplied by the caller (like `EmptyState`'s `action`) so that
 * client-router links work without `@quovy/ui` depending on a router.
 *
 * See `docs/FRONTEND.md` → "Errors must carry their resolution".
 */

/** How the user gets out of the problem. No member of this union means "none". */
export type ProblemResolution =
  /** A control that performs the fix — a Button, a router Link, a dialog trigger. */
  | { kind: "action"; node: React.ReactNode }
  /** The fix is a control already visible in this view; `howToResolve` names it. */
  | { kind: "in-place" }
  /** This viewer lacks permission. `who` says who to ask — required, non-empty. */
  | { kind: "blocked"; who: string };

export interface ProblemNoticeProps {
  /** `error` when it is broken now, `warning` when it will break. Default `error`. */
  tone?: Extract<NoticeTone, "error" | "warning">;
  /** Headline of the title bar. Omit only when the message is a single short sentence. */
  title?: React.ReactNode;
  /** WHAT is wrong — a complete sentence, specific, naming the thing. Opens the message paragraph. */
  children: React.ReactNode;
  /** HOW to resolve it — one imperative sentence, appended to the same paragraph. Required. */
  howToResolve: React.ReactNode;
  /** The way out. Required; see {@link ProblemResolution}. */
  resolution: ProblemResolution;
  /** Announce on appearance; see {@link Notice}'s `live`. */
  live?: boolean;
  className?: string;
}

export function ProblemNotice({
  tone = "error",
  title,
  children,
  howToResolve,
  resolution,
  live,
  className,
}: ProblemNoticeProps) {
  return (
    <Notice
      tone={tone}
      // Always a banner: `appearance="inline"` cannot carry actions.
      appearance="banner"
      {...(title !== undefined ? { title } : {})}
      {...(live !== undefined ? { live } : {})}
      {...(className !== undefined ? { className } : {})}
      {...(resolution.kind === "action" ? { actions: resolution.node } : {})}
    >
      <p data-slot="problem-notice-message">
        <span data-slot="problem-notice-cause">{children}</span>{" "}
        <span data-slot="problem-notice-resolution">{howToResolve}</span>
        {resolution.kind === "blocked" ? (
          <>
            {" "}
            <span data-slot="problem-notice-blocked">{resolution.who}</span>
          </>
        ) : null}
      </p>
    </Notice>
  );
}

export interface ReloadNoticeProps {
  /** What failed to load, plural and lower-case: "threads", "tasks". */
  what: string;
  onReload: () => void;
  /** Overrides the default "Your … could not be loaded." */
  children?: React.ReactNode;
}

/** The common list failure: a load that a retry may fix, with the retry one click away. */
export function ReloadNotice({ what, onReload, children }: ReloadNoticeProps) {
  return (
    <ProblemNotice
      howToResolve={`Reload ${what} to try loading this list again.`}
      resolution={{
        kind: "action",
        node: (
          <Button size="sm" variant="outline" onClick={onReload}>
            Reload {what}
          </Button>
        ),
      }}
    >
      {children ?? `Your ${what} could not be loaded.`}
    </ProblemNotice>
  );
}
